
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface OrderWorkflowStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  timestamp?: string;
  showButton?: boolean;
  buttonText?: string;
  buttonAction?: string;
}

export const useOrderWorkflow = (orderId: string) => {
  return useQuery({
    queryKey: ['order-workflow', orderId],
    queryFn: async () => {
      const { data: order, error } = await supabase
        .from('orders')
        .select(`
          *,
          delivery_address_record:delivery_addresses(
            id,
            contact_name,
            company_name,
            address_line_1,
            address_line_2,
            city,
            postal_code,
            country,
            phone_number
          ),
          sale:sales(*)
        `)
        .eq('id', orderId)
        .single();

      if (error) throw error;

      // Determine if this is a credit-only order
      const isCreditOnly = order.order_type === 'credits_only';

      // Generate workflow steps based on order type
      const steps: OrderWorkflowStep[] = [
        {
          id: 'payment',
          name: 'Payment Received',
          description: 'Customer payment has been processed',
          status: order.status === 'order_processing' || 
                 order.status === 'in_fulfillment' || 
                 order.status === 'ready_for_shipping' || 
                 order.status === 'shipped' || 
                 order.status === 'delivered' ? 'completed' : 'pending',
          timestamp: order.sale?.status === 'paid' ? order.sale.updated_at : undefined,
          showButton: order.status === 'waiting_for_payment',
          buttonText: 'Mark as Paid',
          buttonAction: 'mark_payment_received'
        },
        {
          id: 'processing',
          name: isCreditOnly ? 'Credit Processing' : 'Order Processing',
          description: isCreditOnly ? 'Credits are being processed and allocated' : 'Order is being prepared for fulfillment',
          status: order.status === 'in_fulfillment' || 
                 order.status === 'ready_for_shipping' || 
                 order.status === 'shipped' || 
                 order.status === 'delivered' ? 'completed' : 
                 order.status === 'order_processing' ? 'pending' : 'pending',
          timestamp: order.status === 'order_processing' ? order.fulfillment_started_at : undefined,
          showButton: order.status === 'order_processing',
          buttonText: isCreditOnly ? 'Complete Credit Processing' : 'Start Fulfillment',
          buttonAction: isCreditOnly ? 'complete_credit_processing' : 'start_fulfillment'
        }
      ];

      // For camera+credit orders, add the full fulfillment workflow
      if (!isCreditOnly) {
        steps.push(
          {
            id: 'fulfillment',
            name: 'In Fulfillment',
            description: 'Items are being packed and prepared',
            status: order.status === 'ready_for_shipping' || 
                   order.status === 'shipped' || 
                   order.status === 'delivered' ? 'completed' : 
                   order.status === 'in_fulfillment' ? 'pending' : 'pending',
            showButton: order.status === 'in_fulfillment',
            buttonText: 'Ready to Ship',
            buttonAction: 'ready_to_ship'
          },
          {
            id: 'shipping',
            name: 'Shipped',
            description: 'Order has been shipped to customer',
            status: order.status === 'shipped' || order.status === 'delivered' ? 'completed' : 'pending',
            timestamp: order.shipped_at,
            showButton: order.status === 'ready_for_shipping',
            buttonText: 'Mark Shipped',
            buttonAction: 'ship'
          },
          {
            id: 'delivery',
            name: 'Delivered',
            description: 'Order has been delivered to customer',
            status: order.status === 'delivered' ? 'completed' : 'pending',
            showButton: order.status === 'shipped',
            buttonText: 'Mark Delivered',
            buttonAction: 'deliver'
          },
          {
            id: 'completion',
            name: 'Completed',
            description: 'Customer has confirmed receipt of order',
            status: order.fulfillment_status === 'completed' ? 'completed' : 'pending',
            timestamp: order.fulfillment_completed_at,
            showButton: order.status === 'delivered' && order.fulfillment_status !== 'completed',
            buttonText: 'Mark Complete',
            buttonAction: 'mark_complete'
          }
        );
      }

      return { order, steps };
    },
  });
};

export const useAdvanceOrderWorkflow = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      orderId, 
      action, 
      metadata 
    }: {
      orderId: string;
      action: 'start_processing' | 'start_fulfillment' | 'complete_credit_processing' | 'ready_to_ship' | 'ship' | 'deliver' | 'mark_complete';
      metadata?: any;
    }) => {
      console.log(`Advancing order workflow: ${orderId} - ${action}`);
      
      let updateData: any = { updated_at: new Date().toISOString() };

      switch (action) {
        case 'start_processing':
          updateData.status = 'order_processing';
          updateData.fulfillment_started_at = new Date().toISOString();
          break;
        case 'start_fulfillment':
          updateData.status = 'in_fulfillment';
          break;
        case 'complete_credit_processing':
          // For credit-only orders, mark as completed once processing is done
          updateData.status = 'delivered';
          updateData.fulfillment_completed_at = new Date().toISOString();
          break;
        case 'ready_to_ship':
          updateData.status = 'ready_for_shipping';
          break;
        case 'ship':
          updateData.status = 'shipped';
          updateData.shipped_at = new Date().toISOString();
          if (metadata?.trackingNumber) {
            updateData.tracking_number = metadata.trackingNumber;
          }
          break;
        case 'deliver':
          updateData.status = 'delivered';
          break;
        case 'mark_complete':
          updateData.fulfillment_status = 'completed';
          updateData.fulfillment_completed_at = new Date().toISOString();
          
          // Also update the linked sale to 'completed' (sync with order completion)
          try {
            const { data: orderForSale } = await supabase
              .from('orders')
              .select('sale_id')
              .eq('id', orderId)
              .single();
              
            if (orderForSale?.sale_id) {
              const { error: saleError } = await supabase
                .from('sales')
                .update({
                  status: 'completed',
                  updated_at: new Date().toISOString()
                })
                .eq('id', orderForSale.sale_id);
                
              if (saleError) {
                console.error(`[mark_complete] Failed to update sale status:`, saleError);
              } else {
                console.log(`[mark_complete] Updated sale ${orderForSale.sale_id} to completed`);
              }
            }
          } catch (saleUpdateError) {
            console.error(`[mark_complete] Error syncing sale status:`, saleUpdateError);
          }
          break;
      }

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', orderId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-workflow'] });
      toast({
        title: "Success",
        description: "Order workflow updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating order workflow:", error);
      toast({
        title: "Error",
        description: "Failed to update order workflow",
        variant: "destructive",
      });
    },
  });
};
